import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller that fetches and displays a participant
 * profile in a modal overlay when a sender name is clicked.
 *
 * Usage:
 *   <span data-controller="participant-profile"
 *         data-participant-profile-url-value="/parents/conversations/8/participant_profile?participant_type=SchoolStaff&participant_id=12"
 *         data-action="click->participant-profile#show"
 *         class="cursor-pointer hover:underline">
 *     Sender Name
 *   </span>
 */
export default class extends Controller {
  static values = { url: String }

  async show(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.urlValue) return

    // Prevent double-open
    if (document.getElementById("participant-profile-modal")) return

    try {
      const response = await fetch(this.urlValue, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
        },
      })

      if (!response.ok) return

      const data = await response.json()
      this._renderModal(data)
    } catch (e) {
      console.error("[participant-profile] fetch error:", e)
    }
  }

  _renderModal(data) {
    const overlay = document.createElement("div")
    overlay.id = "participant-profile-modal"
    overlay.className = "fixed inset-0 z-[9999] flex items-center justify-center"
    overlay.style.background = "rgba(15,26,36,.45)"
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) overlay.remove()
    })

    const initials = this._initials(data.first_name, data.last_name)
    const fullName = `${(data.last_name || "").toUpperCase()} ${data.first_name || ""}`.trim()
    const roleLabel = this._roleLabel(data.role)
    const roleChip = this._roleChip(data.role)

    // Séparateur de section — grammaire DS (bordure --ds-border).
    const sectionStart = `<div style="border-top:1px solid var(--ds-border); padding-top:14px; margin-top:14px;">`
    const sectionTitle = (label) => `<p class="text-xs font-semibold uppercase mb-2" style="color:var(--ds-text-3); letter-spacing:.04em;">${label}</p>`

    let sectionsHTML = ""

    // Contact
    if (data.email || data.phone) {
      sectionsHTML += `<div style="border-top:1px solid var(--ds-border); padding-top:14px; margin-top:14px; display:flex; flex-direction:column; gap:8px;">`
      if (data.email) {
        sectionsHTML += `
          <a href="mailto:${this._escAttr(data.email)}" class="flex items-center gap-2 text-sm hover:underline" style="color:var(--ds-text-1);">
            <svg class="w-4 h-4 shrink-0" style="color:var(--ds-text-4);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            ${this._esc(data.email)}
          </a>`
      }
      if (data.phone) {
        sectionsHTML += `
          <a href="tel:${this._escAttr(data.phone)}" class="flex items-center gap-2 text-sm hover:underline" style="color:var(--ds-text-1);">
            <svg class="w-4 h-4 shrink-0" style="color:var(--ds-text-4);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
            ${this._esc(data.phone)}
          </a>`
      }
      sectionsHTML += `</div>`
    }

    // Subjects (teacher)
    if (data.subjects && data.subjects.length > 0) {
      sectionsHTML += `
        ${sectionStart}
          ${sectionTitle(this._t("subjects"))}
          <div class="flex flex-wrap gap-1.5">
            ${data.subjects.map(s => `<span class="ds-chip ds-chip--neutral ds-chip--sm">${this._esc(s)}</span>`).join("")}
          </div>
        </div>`
    }

    // Classrooms
    if (data.classrooms && data.classrooms.length > 0) {
      sectionsHTML += `
        ${sectionStart}
          ${sectionTitle(this._t("classrooms"))}
          <div class="flex flex-wrap gap-1.5">
            ${data.classrooms.map(c => `<span class="ds-chip ds-chip--neutral ds-chip--sm">${this._esc(c)}</span>`).join("")}
          </div>
        </div>`
    }

    // Children (parent)
    if (data.children && data.children.length > 0) {
      sectionsHTML += `
        ${sectionStart}
          ${sectionTitle(this._t("children"))}
          <div style="display:flex; flex-direction:column; gap:8px;">
            ${data.children.map(child => `
              <div class="flex items-center gap-3 p-2" style="background:var(--ds-surface-2); border-radius:var(--ds-radius-md);">
                <span class="ds-avatar ds-avatar--slate shrink-0" style="width:32px; height:32px; font-size:11px;">
                  ${this._esc(child.name?.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase() || "")}
                </span>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium truncate" style="color:var(--ds-text-1);">${this._esc(child.name)}</p>
                  ${child.registration_number ? `<p class="text-xs" style="color:var(--ds-text-4);">${this._esc(child.registration_number)}</p>` : ""}
                  ${child.classrooms?.length ? `<p class="text-xs" style="color:var(--ds-text-3);">${child.classrooms.map(c => this._esc(c)).join(", ")}</p>` : ""}
                </div>
              </div>
            `).join("")}
          </div>
        </div>`
    }

    const card = document.createElement("div")
    card.className = "ds-card w-[360px] max-h-[80vh] overflow-y-auto relative"
    card.style.padding = "24px"
    card.style.boxShadow = "var(--ds-shadow-4)"
    card.innerHTML = `
      <button type="button" class="absolute top-3 right-3" style="color:var(--ds-text-4);" data-action="click->participant-profile#close">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
      <div class="text-center">
        ${data.avatar_url
          ? `<img src="${this._escAttr(data.avatar_url)}" class="w-16 h-16 rounded-full object-cover mx-auto" onerror="this.style.display='none';this.nextElementSibling.style.display='inline-flex'">`
            + `<span class="ds-avatar ds-avatar--green mx-auto" style="width:64px; height:64px; font-size:20px; display:none">${this._esc(initials)}</span>`
          : `<span class="ds-avatar ds-avatar--green mx-auto" style="width:64px; height:64px; font-size:20px; display:inline-flex">${this._esc(initials)}</span>`
        }
        <p class="mt-3 text-lg font-semibold" style="color:var(--ds-text-1);">${this._esc(fullName)}</p>
        <div class="mt-1"><span class="ds-chip ds-chip--md ${roleChip}">${this._esc(roleLabel)}</span></div>
        ${data.title ? `<p class="text-xs mt-1" style="color:var(--ds-text-3);">${this._esc(data.title)}</p>` : ""}
      </div>
      ${sectionsHTML}
    `

    // Close on Escape
    const onEscape = (e) => {
      if (e.key === "Escape") {
        overlay.remove()
        document.removeEventListener("keydown", onEscape)
      }
    }
    document.addEventListener("keydown", onEscape)

    // La carte est injectée dans <body>, hors du périmètre du contrôleur Stimulus :
    // le `data-action` du bouton ✕ n'y est donc pas câblé → on lie un listener direct.
    const closeBtn = card.querySelector('[data-action="click->participant-profile#close"]')
    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        overlay.remove()
        document.removeEventListener("keydown", onEscape)
      })
    }

    overlay.appendChild(card)
    document.body.appendChild(overlay)
  }

  close() {
    document.getElementById("participant-profile-modal")?.remove()
  }

  _initials(first, last) {
    const f = (first || "")[0] || ""
    const l = (last || "")[0] || ""
    return (f + l).toUpperCase()
  }

  _roleLabel(role) {
    const locale = document.documentElement.lang || "fr"
    const labels = {
      fr: { parent: "Parent", teacher: "Enseignant", admin: "Administration" },
      en: { parent: "Parent", teacher: "Teacher", admin: "Administration" },
    }
    return (labels[locale] || labels.fr)[role] || role
  }

  _roleChip(role) {
    switch (role) {
      case "parent": return "ds-chip--info"
      case "teacher": return "ds-chip--success"
      case "admin": return "ds-chip--warning"
      default: return "ds-chip--neutral"
    }
  }

  _t(key) {
    const locale = document.documentElement.lang || "fr"
    const translations = {
      fr: { subjects: "Matières", classrooms: "Classes", children: "Enfants" },
      en: { subjects: "Subjects", classrooms: "Classrooms", children: "Children" },
    }
    return (translations[locale] || translations.fr)[key] || key
  }

  _esc(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  // Escape for use inside HTML attribute values (also escapes quotes)
  _escAttr(str) {
    if (!str) return ""
    return str
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }
}
